import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";

import { useListNotificationHandlersNotificationHandlersGetQuery } from "api";
import { NotificationHandlerPermissionEnum } from "authorization/types";
import { SectionCard } from "components/SectionCard/SectionCard";
import { TooltipWithLock } from "components/TooltipWithLock/TooltipWithLock";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { IZone } from "interfaces/zone.interface";
import { mapById } from "utils/mapById";

import { NotificationHandlerDialog } from "./dialogs/NotificationHandlerDialog";
import { NotificationHandlerList } from "./NotificationHandlerList";

type NotificationHandlersProps = {
  zone: IZone;
  zones: IZone[];
};

export function NotificationHandlers({
  zone,
  zones,
}: NotificationHandlersProps) {
  const { t } = useTranslation();
  const { permissions } = useAuthenticatedUser();
  const [showCreateHandler, setShowCreateHandler] = useState(false);

  const [includeDisabledHandlers, setIncludeDisabledHandlers] = useState(false);

  const { data: notificationData } =
    useListNotificationHandlersNotificationHandlersGetQuery({
      zoneId: zone.id,
    });

  const notifications =
    notificationData?.data?.filter(
      (r) => r.enabled || includeDisabledHandlers,
    ) ?? [];

  const allHandlersAreDisabled = notifications?.length
    ? notifications.every((item) => !item.enabled)
    : false;

  const hasHandlerUpdatePermission = permissions.includes(
    NotificationHandlerPermissionEnum["notification_handler.create"],
  );

  return (
    <SectionCard title={t("notification_handlers")}>
      <Box display={"flex"} width={"100%"} justifyContent="flex-end">
        <FormControlLabel
          label={t("include_disabled_handlers")}
          onChange={(_, val) => setIncludeDisabledHandlers(val)}
          control={<Checkbox checked={includeDisabledHandlers} />}
        />
      </Box>
      {notifications?.length ? (
        <NotificationHandlerList
          zoneId={zone.id}
          zoneEntity={mapById(zones)}
          hasHandlerUpdatePermission={hasHandlerUpdatePermission}
          notificationHandlers={notifications}
        />
      ) : (
        <Typography>{t("there_are_no_handlers_to_display")}</Typography>
      )}
      {allHandlersAreDisabled && (
        <Typography>{t("all_handler_disabled_info_message")}</Typography>
      )}
      {hasHandlerUpdatePermission ? (
        <Button
          variant={"text"}
          onClick={() => setShowCreateHandler(true)}
          disabled={!hasHandlerUpdatePermission}
        >
          {t("add_a_notification_handler")}
        </Button>
      ) : (
        <TooltipWithLock
          title={t("you_do_not_have_permission_to_create_new_handler")}
        >
          <span>
            <Button variant={"text"} disabled onClick={() => {}}>
              {t("add_a_notification_handler")}
            </Button>
          </span>
        </TooltipWithLock>
      )}
      <NotificationHandlerDialog
        isOpen={showCreateHandler}
        onClose={() => setShowCreateHandler(false)}
        zoneId={zone.id}
      />
    </SectionCard>
  );
}
